/**
 * ArticleView — one help article: title, kind and review date, the body (steps, images), the standard
 * not-legal-advice note for a legal-adjacent article (BR-5), and related articles. Used by the panel and
 * by the /help/:id page; opening or leaving it never navigates the screen behind the panel (BR-12).
 */

import { useCallback, useEffect, useMemo, useRef, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Marked } from 'marked';
import { asCalendarDateString, formatCalendarDate } from '@resida/shared';
import type { ArticleView as ArticleViewData } from '@resida/api-types/help';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { ApiClientError } from '../../lib/api.core';
import { useHelpArticle } from '../../lib/api.help';
import { ArticleRow, HELP_HEADING_CLASS, HelpEyebrow, HelpLoadError } from './ArticleList';

/** The standard note on a legal-adjacent article (design brief: Panel, article view). */
const LEGAL_NOTE = 'Resida provides lease templates and tools, not legal advice. Ask an attorney about requirements that apply to you.';

const KIND_LABELS: Record<ArticleViewData['kind'], string> = {
  'how-to': 'How-to',
  faq: 'FAQ',
  concept: 'Concept',
  troubleshooting: 'Troubleshooting',
};

const markdown = new Marked({ gfm: true, async: false });

const EXTERNAL_URL = /^https?:\/\//i;

/**
 * Article markdown to sanitized HTML. Images get lazy loading and a width cap so they never push the
 * column past its edge (BR-53); article-body headings drop to h3 because the article title is the h1/h2.
 */
function renderArticleBody(body: string): string {
  const html = markdown.parse(body) as string;
  const fragment = DOMPurify.sanitize(html, { RETURN_DOM_FRAGMENT: true });

  for (const image of fragment.querySelectorAll('img')) {
    image.setAttribute('loading', 'lazy');
    image.setAttribute('decoding', 'async');
    image.setAttribute('class', 'my-3 h-auto max-w-full rounded-lg border border-border');
    if (!image.hasAttribute('alt')) image.setAttribute('alt', '');
  }
  for (const link of fragment.querySelectorAll('a[href]')) {
    if (EXTERNAL_URL.test(link.getAttribute('href') ?? '')) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    }
  }
  for (const heading of fragment.querySelectorAll('h1, h2')) {
    const replacement = document.createElement('h3');
    replacement.append(...heading.childNodes);
    heading.replaceWith(replacement);
  }

  const container = document.createElement('div');
  container.append(fragment);
  return container.innerHTML;
}

/** The article id a link points at when it is an in-app help link (`/help/<id>`), else null. */
function helpArticleIdFromHref(href: string): string | null {
  const path = href.split(/[?#]/, 1)[0] ?? '';
  return /^(?:\/landlord)?\/help\/([a-z0-9-]+)\/?$/.exec(path)?.[1] ?? null;
}

interface ArticleBodyProps {
  html: string;
  onSelectArticle?: (articleId: string) => void;
}

function ArticleBody({ html, onSelectArticle }: ArticleBodyProps) {
  const navigate = useNavigate();

  // Links in the body are plain anchors; in-app ones are routed here so they open in place (panel) or
  // through the router (page) instead of reloading the app.
  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as HTMLElement).closest('a');
    const href = anchor?.getAttribute('href');
    if (!anchor || !href || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    if (EXTERNAL_URL.test(href) || !href.startsWith('/') || href.startsWith('//')) return;
    event.preventDefault();
    const articleId = helpArticleIdFromHref(href);
    if (articleId && onSelectArticle) onSelectArticle(articleId);
    else navigate(href.startsWith('/landlord/') ? href.slice('/landlord'.length) : href);
  };

  return (
    <div
      data-testid="help-article-body"
      onClick={handleClick}
      className={
        'break-words text-sm leading-relaxed text-foreground [&_p]:my-2 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 ' +
        '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-2 [&_h3]:mb-1 [&_h3]:mt-4 [&_h3]:text-base [&_h3]:font-semibold ' +
        '[&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_strong]:font-semibold [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 ' +
        '[&_pre]:overflow-x-auto [&_table]:block [&_table]:overflow-x-auto'
      }
      // Sanitized by renderArticleBody (DOMPurify) before it gets here.
      // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

type FocusRef = (element: HTMLElement | null) => void;

interface ArticleViewProps {
  articleId: string;
  /** In the panel related and in-body links open in place; without it they navigate to /help/<id>. */
  onSelectArticle?: (articleId: string) => void;
  /** In the panel the retired notice's help-center link returns to the panel's article list in place (BR-12); without it the link goes to /help. */
  onOpenHelpCenter?: () => void;
  /** The page's title is its h1; inside the panel the title is an h2. */
  headingLevel?: 'h1' | 'h2';
  /** Moves keyboard focus to the article's heading when it opens (panel only; the page keeps its own focus). */
  focusHeading?: boolean;
}

function ArticleSkeleton({ focusRef }: { focusRef: FocusRef }) {
  return (
    <div
      ref={focusRef}
      tabIndex={-1}
      className="flex flex-col gap-3 focus:outline-none"
      data-testid="help-article-loading"
      aria-busy="true"
      aria-label="Loading"
    >
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}

export function ArticleView({ articleId, onSelectArticle, onOpenHelpCenter, headingLevel = 'h2', focusHeading = false }: ArticleViewProps) {
  const { data, isLoading, isError, error, refetch } = useHelpArticle(articleId);
  const Heading = headingLevel;
  const bodyHtml = useMemo(() => (data?.status === 'current' ? renderArticleBody(data.body) : ''), [data]);

  // Whichever of the loading block or the heading is showing takes focus, so the view that replaced the
  // list never leaves focus on <body>.
  const focusTarget = useRef<HTMLElement | null>(null);
  const focusRef = useCallback<FocusRef>((element) => {
    focusTarget.current = element;
  }, []);
  useEffect(() => {
    if (focusHeading) focusTarget.current?.focus();
  }, [focusHeading, articleId, isLoading]);

  if (isLoading) return <ArticleSkeleton focusRef={focusRef} />;

  if (isError || !data) {
    if (error instanceof ApiClientError && error.status === 404) {
      return (
        <div className="flex flex-col gap-2" data-testid="help-article-not-found">
          <Heading ref={focusRef} tabIndex={-1} className={HELP_HEADING_CLASS}>
            This article isn't available
          </Heading>
          <p className="text-sm text-muted-foreground">It may have been moved or removed. Search the help center to find it.</p>
        </div>
      );
    }
    return (
      <HelpLoadError
        message="We couldn't load this article."
        onRetry={() => void refetch()}
        data-testid="help-article-error"
      />
    );
  }

  if (data.status === 'retired') {
    return (
      <div className="flex flex-col gap-3" data-testid="help-article-retired">
        <Heading ref={focusRef} tabIndex={-1} className={HELP_HEADING_CLASS}>
          {data.title}
        </Heading>
        <p className="text-sm text-muted-foreground">This article has been retired.</p>
        {data.replacedBy && (
          <section className="flex flex-col" aria-labelledby="help-replaced-by">
            <HelpEyebrow id="help-replaced-by">Replaced by</HelpEyebrow>
            <ArticleRow
              articleId={data.replacedBy.id}
              title={data.replacedBy.title}
              onSelect={onSelectArticle}
              data-testid={`help-article-replacement-${data.replacedBy.id}`}
            />
          </section>
        )}
        {/* BR-6: a retired article with no replacement points to the help center. */}
        {!data.replacedBy && (
          <Link
            to="/help"
            data-testid="help-article-retired-help-center"
            onClick={(event) => {
              if (!onOpenHelpCenter || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              onOpenHelpCenter();
            }}
            className="inline-flex min-h-[44px] items-center self-start rounded text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Browse the help center
          </Link>
        )}
      </div>
    );
  }

  return (
    <article className="flex flex-col gap-4" data-testid="help-article">
      <header className="flex flex-col gap-2">
        <Heading ref={focusRef} tabIndex={-1} className={HELP_HEADING_CLASS} data-testid="help-article-title">
          {data.title}
        </Heading>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="primary" data-testid="help-article-kind">
            {KIND_LABELS[data.kind]}
          </Badge>
          <span className="text-xs text-muted-foreground" data-testid="help-article-reviewed">
            Last reviewed {formatCalendarDate(asCalendarDateString(data.reviewedAt))}
          </span>
        </div>
      </header>

      <ArticleBody html={bodyHtml} onSelectArticle={onSelectArticle} />

      {data.legalAdjacent && (
        <div
          role="note"
          data-testid="help-legal-note"
          className="flex gap-2.5 rounded-lg border border-info/25 bg-info/10 p-3 text-[13px]"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden="true" />
          <span className="text-foreground">{LEGAL_NOTE}</span>
        </div>
      )}

      {data.related.length > 0 && (
        <section aria-labelledby="help-related" className="flex flex-col" data-testid="help-related">
          <HelpEyebrow id="help-related">Related</HelpEyebrow>
          {data.related.map((related) => (
            <ArticleRow
              key={related.id}
              articleId={related.id}
              title={related.title}
              onSelect={onSelectArticle}
              data-testid={`help-related-${related.id}`}
            />
          ))}
        </section>
      )}
    </article>
  );
}

